import { Badge, Box, Divider, Flex, Text, VStack, Wrap, WrapItem } from '@chakra-ui/react'
import {
  AntepartumRisk,
  FieldMapValue,
  PatientEducationTopic,
  PlanItem,
  PlanItemCategory,
  Problem,
  ProblemStatus,
  ReferralTarget,
} from '@hb/shared'
import { useMemo } from 'react'
import { useICD10Groups } from '../../../../hooks/backend/icd10/useICD10Groups'

const riskColorScheme: Record<AntepartumRisk, string> = {
  low: 'green',
  moderate: 'orange',
  high: 'red',
}

const referralLabels: Record<ReferralTarget, string> = {
  mfm: 'MFM',
  ob: 'OB',
  cardiology: 'Cardiology',
  nutrition: 'Nutrition',
  'social-work': 'Social Work',
  'mental-health': 'Mental Health',
  other: 'Other',
}

const educationLabels: Record<PatientEducationTopic, string> = {
  'birth-plan': 'Birth plan',
  'signs-of-labor': 'Signs of labor',
  'kick-counts': 'Kick counts',
  'gbs-results': 'GBS results',
  nutrition: 'Nutrition',
  breastfeeding: 'Breastfeeding',
}

const returnVisitLabels: Record<string, string> = {
  '1-week': '1 Week',
  '2-weeks': '2 Weeks',
  '4-weeks': '4 Weeks',
  prn: 'PRN',
}

const planItemCategoryLabels: Record<PlanItemCategory, string> = {
  labs: 'Labs',
  referral: 'Referral',
  education: 'Education',
  'return-visit': 'Return Visit',
  rx: 'Prescription',
  procedure: 'Procedure',
}

const planItemCategoryColors: Record<PlanItemCategory, string> = {
  labs: 'purple',
  referral: 'blue',
  education: 'teal',
  'return-visit': 'green',
  rx: 'orange',
  procedure: 'red',
}

const problemStatusColors: Record<ProblemStatus, string> = {
  active: 'red',
  resolved: 'green',
  deferred: 'gray',
}

const problemStatusLabels: Record<ProblemStatus, string> = {
  active: 'Active',
  resolved: 'Resolved',
  deferred: 'Deferred',
}

const LabelText = ({ label }: { label: string }) => (
  <Text fontSize="xs" fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={0.5}>
    {label}
  </Text>
)

const ProblemListView = ({ problems }: { problems: Problem[] }) => {
  const active = problems.filter(p => p.status === 'active')
  const deferred = problems.filter(p => p.status === 'deferred')
  const resolved = problems.filter(p => p.status === 'resolved')
  const ordered = [...active, ...deferred, ...resolved]

  return (
    <Box w="100%">
      <LabelText label="Problem List" />
      <VStack align="stretch" spacing={1.5}>
        {ordered.map(p => (
          <Flex key={p.id} align="center" gap={2} opacity={p.status === 'resolved' ? 0.65 : 1}>
            <Badge
              colorScheme={problemStatusColors[p.status]}
              fontSize="2xs"
              px={1.5}
              py={0.5}
              flexShrink={0}>
              {problemStatusLabels[p.status]}
            </Badge>
            <Text
              fontSize="sm"
              textDecoration={p.status === 'resolved' ? 'line-through' : 'none'}
              color={p.status === 'resolved' ? 'gray.500' : undefined}>
              {p.description || (
                <Text as="span" color="gray.400" fontStyle="italic">
                  No description
                </Text>
              )}
            </Text>
            {p.notes && (
              <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }}>
                — {p.notes}
              </Text>
            )}
          </Flex>
        ))}
      </VStack>
    </Box>
  )
}

const PlanItemsView = ({ planItems }: { planItems: PlanItem[] }) => (
  <Box w="100%">
    <LabelText label="Plan" />
    <VStack align="stretch" spacing={1.5}>
      {planItems.map((item, i) => (
        <Flex key={i} align="flex-start" gap={2}>
          <Badge
            colorScheme={planItemCategoryColors[item.category] ?? 'gray'}
            fontSize="2xs"
            px={1.5}
            py={0.5}
            flexShrink={0}
            mt={0.5}>
            {planItemCategoryLabels[item.category] ?? item.category}
          </Badge>
          <Box flex={1}>
            <Text fontSize="sm">{item.text}</Text>
            {item.followUpDate && (
              <Text fontSize="xs" color="gray.500" _dark={{ color: 'gray.400' }}>
                Follow-up: {item.followUpDate}
              </Text>
            )}
          </Box>
        </Flex>
      ))}
    </VStack>
  </Box>
)

export const AssessmentPlanView = ({ data }: { data: FieldMapValue }) => {
  const activeGroups = useICD10Groups()
  const icdDescriptionMap = useMemo(
    () => new Map(activeGroups.flatMap(g => g.codes.map(c => [c.code, c.description]))),
    [activeGroups],
  )

  const assessment = data?.assessment as string | undefined
  const antepartumRisk = data?.antepartumRisk as AntepartumRisk | undefined
  const antepartumRiskReason = data?.antepartumRiskReason as string | undefined
  const problemList = data?.problemList as Problem[] | undefined
  const planItems = data?.planItems as PlanItem[] | undefined
  const referrals = data?.referrals as ReferralTarget[] | undefined
  const patientEducation = data?.patientEducation as PatientEducationTopic[] | undefined
  const returnVisitInterval = data?.returnVisitInterval as string | undefined
  const icd10Codes = data?.icd10Codes as string[] | undefined

  const displayCodes = useMemo(
    () => (icd10Codes ?? []).filter(c => !c.startsWith('Z3A.')),
    [icd10Codes],
  )

  return (
    <VStack align="flex-start" spacing={3} w="100%">
      {assessment && (
        <Box w="100%">
          <LabelText label="Assessment" />
          <Text fontSize="sm" whiteSpace="pre-wrap">
            {assessment}
          </Text>
        </Box>
      )}

      {antepartumRisk && (
        <Box>
          <LabelText label="Risk Stratification" />
          <Flex align="center" gap={2}>
            <Badge colorScheme={riskColorScheme[antepartumRisk]} fontSize="sm" px={2} py={0.5}>
              {antepartumRisk.charAt(0).toUpperCase() + antepartumRisk.slice(1)}
            </Badge>
            {antepartumRiskReason && (
              <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                — {antepartumRiskReason}
              </Text>
            )}
          </Flex>
        </Box>
      )}

      {problemList && problemList.length > 0 && (
        <>
          <Divider />
          <ProblemListView problems={problemList} />
        </>
      )}

      {planItems && planItems.length > 0 && (
        <>
          <Divider />
          <PlanItemsView planItems={planItems} />
        </>
      )}

      {referrals && referrals.length > 0 && (
        <Box>
          <LabelText label="Referrals" />
          <Wrap spacing={1}>
            {referrals.map(r => (
              <WrapItem key={r}>
                <Badge colorScheme="blue" variant="outline">
                  {referralLabels[r] ?? r}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}

      {patientEducation && patientEducation.length > 0 && (
        <Box>
          <LabelText label="Patient Education" />
          <Wrap spacing={1}>
            {patientEducation.map(t => (
              <WrapItem key={t}>
                <Badge colorScheme="teal" variant="outline">
                  {educationLabels[t] ?? t}
                </Badge>
              </WrapItem>
            ))}
          </Wrap>
        </Box>
      )}

      {returnVisitInterval && (
        <Box>
          <LabelText label="Return Visit" />
          <Text fontSize="sm">{returnVisitLabels[returnVisitInterval] ?? returnVisitInterval}</Text>
        </Box>
      )}

      {displayCodes.length > 0 && (
        <Box>
          <LabelText label="Diagnoses" />
          <VStack align="flex-start" spacing={0.5}>
            {displayCodes.map(code => (
              <Text key={code} fontSize="sm">
                {icdDescriptionMap.get(code) ?? code}
              </Text>
            ))}
          </VStack>
        </Box>
      )}
    </VStack>
  )
}
